import { useState } from "react";
import { Send } from "lucide-react";
import { waLink, PHONE_DISPLAY } from "../data/site";

const inputCls =
  "w-full rounded-lg border border-[#e2ded4] bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d6a4f]/40";

const Field = ({ label, id, required = true, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold text-[#2d3748] mb-1.5">
      {label} {required && "*"}
    </label>
    {children}
  </div>
);

// mode: "contact" (product interest) or "dealer" (partnership enquiry)
export const EnquiryForm = ({ mode = "contact", defaultProduct = "", testidPrefix = "enquiry" }) => {
  const isDealer = mode === "dealer";
  const [form, setForm] = useState({
    name: "",
    business: "",
    phone: "",
    city: "",
    interest: isDealer ? "Dealer / Distributor" : defaultProduct || "Green Leaf Plates",
    products: "Green Leaf Plates",
    requirement: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      isDealer
        ? "New Partnership Enquiry — Organik Things Website"
        : "New Business Enquiry — Organik Things Website",
      `Name: ${form.name}`,
      `Business Name: ${form.business}`,
      `Phone: ${form.phone}`,
      `${isDealer ? "City & State" : "City"}: ${form.city}`,
      isDealer
        ? `Business Type: ${form.interest}\nProducts of Interest: ${form.products}`
        : `Interested Product: ${form.interest}`,
      isDealer && form.requirement && `Expected Order Requirement: ${form.requirement}`,
      form.message && `Message: ${form.message}`,
    ].filter(Boolean);
    window.open(waLink(lines.join("\n")), "_blank");
    setSent(true);
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-xl border border-[#e2ded4] p-6 sm:p-7 space-y-4"
      data-testid={`${testidPrefix}-form`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name" id={`${testidPrefix}-name`}>
          <input id={`${testidPrefix}-name`} required value={form.name} onChange={update("name")}
            data-testid={`${testidPrefix}-input-name`} className={inputCls} placeholder="Your full name" />
        </Field>
        <Field label="Business Name" id={`${testidPrefix}-business`}>
          <input id={`${testidPrefix}-business`} required value={form.business} onChange={update("business")}
            data-testid={`${testidPrefix}-input-business`} className={inputCls} placeholder="Your business name" />
        </Field>
        <Field label="Phone Number" id={`${testidPrefix}-phone`}>
          <input id={`${testidPrefix}-phone`} type="tel" required value={form.phone} onChange={update("phone")}
            data-testid={`${testidPrefix}-input-phone`} className={inputCls} placeholder="+91 ..." />
        </Field>
        <Field label={isDealer ? "City & State" : "City"} id={`${testidPrefix}-city`}>
          <input id={`${testidPrefix}-city`} required value={form.city} onChange={update("city")}
            data-testid={`${testidPrefix}-input-city`} className={inputCls}
            placeholder={isDealer ? "e.g. Indore, Madhya Pradesh" : "Your city"} />
        </Field>
      </div>
      <div className={isDealer ? "grid grid-cols-1 sm:grid-cols-2 gap-4" : ""}>
        <Field label={isDealer ? "Business Type" : "Interested Product"} id={`${testidPrefix}-interest`}>
          <select id={`${testidPrefix}-interest`} value={form.interest} onChange={update("interest")}
            data-testid={`${testidPrefix}-select-interest`} className={inputCls}>
            {isDealer ? (
              <>
                <option>Dealer / Distributor</option>
                <option>Wholesaler</option>
                <option>Retailer</option>
                <option>Caterer / Event Organiser</option>
                <option>Hotel / Restaurant / Cafe</option>
                <option>White-Label Partner</option>
                <option>Other Bulk Buyer</option>
              </>
            ) : (
              <>
                <option>Green Leaf Plates</option>
                <option>Leaf Bowls & Dona-Pattal</option>
                <option>Leaf Thalis & Partition Plates</option>
                <option>Grass Straws</option>
                <option>Dealership / Distribution Partnership</option>
                <option>White-Label Partnership</option>
                <option>All Products</option>
              </>
            )}
          </select>
        </Field>
        {isDealer && (
          <Field label="Products of Interest" id={`${testidPrefix}-products`}>
            <select id={`${testidPrefix}-products`} value={form.products} onChange={update("products")}
              data-testid={`${testidPrefix}-select-products`} className={inputCls}>
              <option>Green Leaf Plates</option>
              <option>Leaf Bowls & Dona-Pattal</option>
              <option>Leaf Thalis & Partition Plates</option>
              <option>Grass Straws</option>
              <option>All Products</option>
            </select>
          </Field>
        )}
      </div>
      {isDealer && (
        <Field label="Expected Order Requirement" id={`${testidPrefix}-requirement`} required={false}>
          <input id={`${testidPrefix}-requirement`} value={form.requirement} onChange={update("requirement")}
            data-testid={`${testidPrefix}-input-requirement`} className={inputCls}
            placeholder="e.g. monthly quantity you have in mind" />
        </Field>
      )}
      <Field label="Message" id={`${testidPrefix}-message`} required={false}>
        <textarea id={`${testidPrefix}-message`} rows={4} value={form.message} onChange={update("message")}
          data-testid={`${testidPrefix}-input-message`} className={inputCls}
          placeholder="Tell us about your requirement..." />
      </Field>
      <button type="submit" data-testid={`${testidPrefix}-submit-button`}
        className="inline-flex items-center justify-center gap-2 w-full bg-[#1b4332] hover:bg-[#2d6a4f] text-white font-semibold px-6 py-3 rounded-lg transition-colors">
        <Send className="w-4 h-4" /> Send Enquiry via WhatsApp
      </button>
      <p className="text-xs text-[#5a6b5c] leading-relaxed" data-testid={`${testidPrefix}-form-note`}>
        This form opens WhatsApp with your enquiry pre-filled — your message goes
        directly to our team. You can also call us at {PHONE_DISPLAY} or email us
        anytime.
      </p>
      {sent && (
        <p className="text-sm font-medium text-[#2d6a4f]" data-testid={`${testidPrefix}-form-success`}>
          WhatsApp opened with your enquiry. If it didn't open, please use the
          WhatsApp button or call us at {PHONE_DISPLAY}.
        </p>
      )}
    </form>
  );
};
